/**
 * CustomerLookupPage — `/customer-service/lookup`. Live · GET /v1/customers/lookup.
 *
 * Mirrors the screens prototype's customer search surface but uses the live
 * service-customer lookup endpoint already wired in mail-register's
 * CustomerLookupAutocomplete.
 */
import { type FormEvent, type JSX, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import { lookupCustomers } from '../../lib/api/customers';
import { DeferredNotice, PageHeader } from '../shared/StaffUI';

export function CustomerLookupPage(): JSX.Element {
  const [phone, setPhone] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [fullName, setFullName] = useState<string>('');
  const [customerNumber, setCustomerNumber] = useState<string>('');
  const [submitted, setSubmitted] = useState<boolean>(false);

  const query = useQuery({
    queryKey: ['cs', 'customers', 'lookup', { phone, email, fullName, customerNumber }],
    queryFn: () =>
      lookupCustomers({
        ...(phone !== '' && { phone }),
        ...(email !== '' && { email }),
        ...(fullName !== '' && { fullName }),
        ...(customerNumber !== '' && { customerNumber }),
        pageSize: 25,
      }),
    enabled: submitted,
    retry: false,
    staleTime: 15_000,
  });

  const denied =
    query.isError && query.error instanceof ApiError && (query.error.status === 401 || query.error.status === 403);

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setSubmitted(true);
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Customer Lookup" description="Find a customer by phone, email or NIN." />

      <form onSubmit={onSubmit} className="bg-white border border-border rounded-xl p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="block text-xs font-medium text-muted mb-1">Phone</span>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+2348012345678" className="w-full px-3 py-2 text-sm border border-border rounded-lg" />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-muted mb-1">Email</span>
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ada@example.com" type="email" className="w-full px-3 py-2 text-sm border border-border rounded-lg" />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-muted mb-1">Full name</span>
          <input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Ada Lovelace" className="w-full px-3 py-2 text-sm border border-border rounded-lg" />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-muted mb-1">Customer number</span>
          <input value={customerNumber} onChange={(e) => setCustomerNumber(e.target.value)} placeholder="CUS-001" className="w-full px-3 py-2 text-sm border border-border rounded-lg" />
        </label>
        <div className="sm:col-span-2 flex justify-end">
          <button type="submit" className="px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark">
            Search
          </button>
        </div>
      </form>

      {!submitted ? (
        <p className="text-sm text-muted">Provide at least one filter and search.</p>
      ) : query.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          The dev token doesn&apos;t carry <code className="font-mono">Customer/auditRead</code>.
        </DeferredNotice>
      ) : query.isError ? (
        <DeferredNotice title="Service unreachable">{(query.error as Error).message}</DeferredNotice>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
              <tr>
                <th className="text-left px-4 py-2">#</th>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Phone</th>
                <th className="text-left px-4 py-2">Email</th>
                <th className="text-left px-4 py-2">Grade</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(query.data?.items ?? []).map((c) => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-mono text-xs text-gray-900">{c.customerNumber}</td>
                  <td className="px-4 py-2 text-xs text-gray-900">{c.fullName}</td>
                  <td className="px-4 py-2 text-xs text-muted">{c.primaryPhone}</td>
                  <td className="px-4 py-2 text-xs text-muted">{c.primaryEmail ?? '—'}</td>
                  <td className="px-4 py-2 text-xs">{c.grade}</td>
                  <td className="px-4 py-2 text-right">
                    <Link to={`/admin/customers/${encodeURIComponent(c.id)}`} className="text-xs font-semibold text-nipost-green hover:underline">
                      Open profile →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(query.data?.items.length ?? 0) === 0 ? (
            <p className="p-8 text-center text-sm text-muted">No customers match.</p>
          ) : null}
        </div>
      )}
    </div>
  );
}
